import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
} from "../../constants/documentManagementConstants";
import { METADATA_STATUS } from "../../services/ragDocumentApi";
import type {
  PreviewItemUi,
  PreviewMetadataField,
} from "../../types/documentManagementUiTypes";

export type DocumentMetadataPreviewProps = {
  items: PreviewItemUi[];
  activeDatasetName?: string;
  saving: boolean;
  deletingDocumentId: number | null;
  bulkDeleteBusy: boolean;
  selectedIdSet: Set<number>;
  onToggleSelect: (documentId: number) => void;
  onToggleSelectAll: () => void;
  onBulkDelete: () => void;
  onUpdateMetadata: (idx: number, field: PreviewMetadataField, value: string) => void;
  onUpdateCategory: (idx: number, category: string) => void;
  onRemoveItem: (idx: number) => void;
  onUploadMore: () => void;
  onConfirm: () => void;
  uploadMoreDisabled?: boolean;
  confirmDisabled?: boolean;
  /** When true, per-row "Detecting…" captions are hidden (section progress panel is shown). */
  hideRowDetectingIndicator?: boolean;
};

export function DocumentMetadataPreview({
  items,
  activeDatasetName,
  saving,
  deletingDocumentId,
  bulkDeleteBusy,
  selectedIdSet,
  onToggleSelect,
  onToggleSelectAll,
  onBulkDelete,
  onUpdateMetadata,
  onUpdateCategory,
  onRemoveItem,
  onUploadMore,
  onConfirm,
  uploadMoreDisabled = false,
  confirmDisabled = false,
  hideRowDetectingIndicator = false,
}: DocumentMetadataPreviewProps) {
  const { t } = useTranslation();

  const selectedCount = useMemo(
    () => items.filter((item) => selectedIdSet.has(item.document_id)).length,
    [items, selectedIdSet]
  );

  const allSelected = items.length > 0 && selectedCount === items.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const selectionDisabled = saving || bulkDeleteBusy || deletingDocumentId !== null;

  if (!items.length) {
    return null;
  }

  return (
    <Box component="section" className="dm-preview-panel">
      <Typography variant="subtitle1" fontWeight={600} color="warning.dark" gutterBottom>
        {t("opva.dmReviewTitle", {
          defaultValue: "Review detected metadata ({{count}} file(s))",
          count: items.length,
        })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        {t("opva.dmReviewBody", {
          defaultValue:
            "Review and edit metadata for each file, choose a category, then confirm to start processing. This list is saved until you confirm or remove uploads.",
        })}
        {activeDatasetName ? (
          <>
            {" "}
            {t("opva.dmReviewDataset", {
              defaultValue: "Dataset: {{name}}.",
              name: activeDatasetName,
            })}
          </>
        ) : null}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          {t("opva.dmReviewSelectionSummary", {
            defaultValue: "{{count}} documents",
            count: items.length,
          })}
          {selectedCount > 0
            ? ` · ${t("opva.dmBulkSelected", {
                defaultValue: "{{count}} selected",
                count: selectedCount,
              })}`
            : ""}
        </Typography>
        <Button
          variant="outlined"
          color="error"
          disabled={selectedCount === 0 || selectionDisabled}
          onClick={onBulkDelete}
          startIcon={
            bulkDeleteBusy ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DeleteIcon fontSize="small" />
            )
          }
          sx={{ fontWeight: 600 }}
        >
          {t("opva.dmDeleteDocuments", {
            defaultValue: "Delete documents",
          })}
        </Button>
      </Box>

      <TableContainer sx={{ mb: 2 }}>
        <Table size="small" className="dm-table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" className="dm-col-checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={selectionDisabled}
                  onChange={onToggleSelectAll}
                  inputProps={{
                    "aria-label": t("opva.dmSelectAllReviewDocuments", {
                      defaultValue: "Select all documents in review",
                    }),
                  }}
                />
              </TableCell>
              <TableCell>{t("opva.dmColFile", { defaultValue: "File" })}</TableCell>
              <TableCell>{t("opva.dmColCompany", { defaultValue: "Company" })}</TableCell>
              <TableCell>{t("opva.dmColProduct", { defaultValue: "Product" })}</TableCell>
              <TableCell>{t("opva.dmColModel", { defaultValue: "Model" })}</TableCell>
              <TableCell>{t("opva.dmColCategory", { defaultValue: "Category" })}</TableCell>
              <TableCell align="center" width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow
                key={item.document_id}
                className={
                  selectedIdSet.has(item.document_id) ? "dm-row--selected" : undefined
                }
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIdSet.has(item.document_id)}
                    disabled={selectionDisabled}
                    onChange={() => onToggleSelect(item.document_id)}
                    inputProps={{
                      "aria-label": t("opva.dmSelectReviewDocument", {
                        defaultValue: "Select {{name}}",
                        name: item.file_name,
                      }),
                    }}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap title={item.file_name}>
                    {item.file_name}
                  </Typography>
                  {item.metadata_pending && !hideRowDetectingIndicator ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CircularProgress size={12} />
                      {t("opva.dmMetadataDetecting", {
                        defaultValue: "Detecting metadata...",
                      })}
                    </Typography>
                  ) : null}
                  {item.metadata_status === METADATA_STATUS.FAILED ? (
                    <Typography variant="caption" color="error">
                      {t("opva.dmMetadataDetectFailed", {
                        defaultValue: "Metadata detection failed — fill in fields manually.",
                      })}
                    </Typography>
                  ) : null}
                  {item.detected_company ||
                  item.detected_product ||
                  item.detected_model ? (
                    <Typography variant="caption" color="primary">
                      {t("opva.dmAiDetected", { defaultValue: "AI detected:" })}{" "}
                      {[item.detected_company, item.detected_product, item.detected_model]
                        .filter(Boolean)
                        .join(" / ")}
                    </Typography>
                  ) : null}
                </TableCell>
                {(["company_name", "product_name", "model_name"] as const).map(
                  (field) => (
                    <TableCell key={field} sx={{ minWidth: 120 }}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="outlined"
                        value={item[field]}
                        onChange={(e) =>
                          onUpdateMetadata(idx, field, e.target.value)
                        }
                        disabled={saving || !!item.metadata_pending}
                        placeholder="—"
                      />
                    </TableCell>
                  )
                )}
                <TableCell sx={{ minWidth: 160 }}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={item.document_category ?? ""}
                      onChange={(e) => onUpdateCategory(idx, e.target.value)}
                      disabled={saving || !!item.metadata_pending}
                      displayEmpty
                      renderValue={(value) =>
                        DOCUMENT_CATEGORY_LABELS[
                          value as keyof typeof DOCUMENT_CATEGORY_LABELS
                        ] ??
                        (value ||
                          t("opva.dmSelectCategory", {
                            defaultValue: "Select category",
                          }))
                      }
                    >
                      {DOCUMENT_CATEGORIES.map((c) => (
                        <MenuItem key={c || "none"} value={c}>
                          {DOCUMENT_CATEGORY_LABELS[c]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    aria-label={t("opva.dmDeleteDocument", {
                      defaultValue: "Delete document",
                    })}
                    disabled={
                      deletingDocumentId === item.document_id ||
                      bulkDeleteBusy ||
                      saving
                    }
                    onClick={() => onRemoveItem(idx)}
                    className="dm-icon-remove"
                  >
                    {deletingDocumentId === item.document_id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DeleteIcon fontSize="small" color="error" />
                    )}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Button
          variant="outlined"
          disabled={saving || uploadMoreDisabled}
          onClick={onUploadMore}
        >
          {t("opva.dmUploadMore", { defaultValue: "Upload more documents" })}
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={saving || confirmDisabled}
          onClick={onConfirm}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {saving
            ? t("opva.dmSaving", { defaultValue: "Saving…" })
            : t("opva.dmConfirmAndProcess", {
                defaultValue: "Confirm and start processing",
              })}
        </Button>
      </Box>
    </Box>
  );
}
